import { FieldPath, collection, doc, onSnapshot, query, updateDoc, type DocumentData, type FirestoreError, type QueryConstraint } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { db, functions } from '@/lib/firebase'

export type DocState<T> = { data: T | null; loading: boolean; error: FirestoreError | null; exists: boolean }

export function useDoc<T = DocumentData>(path: string | null): DocState<T> {
  const [state, setState] = useState<DocState<T>>({ data: null, loading: Boolean(path), error: null, exists: false })
  useEffect(() => {
    if (!path) {
      setState({ data: null, loading: false, error: null, exists: false })
      return
    }
    setState((s) => ({ ...s, loading: true }))
    return onSnapshot(
      doc(db, path),
      (snap) => setState({ data: (snap.data() as T) ?? null, loading: false, error: null, exists: snap.exists() }),
      (error) => setState({ data: null, loading: false, error, exists: false }),
    )
  }, [path])
  return state
}

export type CollectionState<T> = { items: Array<T & { id: string }>; loading: boolean; error: FirestoreError | null }

/**
 * `deps`: values used inside `constraints` (a `where` argument, say). Constraints are built inline, so the
 * subscription is keyed by their types plus these values rather than by identity.
 */
export function useCollection<T = DocumentData>(path: string | null, constraints: QueryConstraint[] = [], deps: unknown[] = []): CollectionState<T> {
  const [state, setState] = useState<CollectionState<T>>({ items: [], loading: Boolean(path), error: null })
  const key = JSON.stringify([constraints.map((c) => c.type), deps])
  useEffect(() => {
    if (!path) {
      setState({ items: [], loading: false, error: null })
      return
    }
    return onSnapshot(
      query(collection(db, path), ...constraints),
      (snap) => setState({ items: snap.docs.map((d) => ({ id: d.id, ...(d.data() as T) })), loading: false, error: null }),
      (error) => setState({ items: [], loading: false, error }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, key])
  return state
}

/* ---------------- global save status (shown in the top bar) ---------------- */
type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'
let globalStatus: SaveStatus = 'idle'
const listeners = new Set<() => void>()
const pendingWriters = new Set<string>()
function setGlobal(next: SaveStatus) {
  if (globalStatus === next) return
  globalStatus = next
  for (const l of listeners) l()
}
function reportWriter(id: string, status: SaveStatus) {
  if (status === 'saving') pendingWriters.add(id)
  else pendingWriters.delete(id)
  if (status === 'error') setGlobal('error')
  else if (pendingWriters.size > 0) setGlobal('saving')
  else setGlobal(status === 'idle' ? globalStatus : 'saved')
}
export function useGlobalSaveStatus(): SaveStatus {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb)
      return () => listeners.delete(cb)
    },
    () => globalStatus,
    () => 'idle' as SaveStatus,
  )
}

/** Slugs may contain '-', which Firestore's dotted-string syntax rejects; build a FieldPath instead. */
export function toFieldPath(dotted: string): FieldPath {
  return new FieldPath(...dotted.split('.'))
}

/** Debounced dotted-path writer for one document. Every field saves independently. */
export function useDocWriter(path: string | null, delayMs = 600) {
  const pending = useRef<Record<string, unknown>>({})
  const timer = useRef<number | null>(null)
  const [localStatus, setLocalStatus] = useState<SaveStatus>('idle')
  const writerId = useRef(`w${Math.random().toString(36).slice(2)}`)
  const setStatus = useCallback((s: SaveStatus) => {
    setLocalStatus(s)
    reportWriter(writerId.current, s)
  }, [])
  const status = localStatus

  const flush = useCallback(async () => {
    if (!path) return
    const updates = pending.current
    pending.current = {}
    if (Object.keys(updates).length === 0) return
    setStatus('saving')
    try {
      const entries = Object.entries(updates)
      const [first, ...rest] = entries
      if (!first) return
      await updateDoc(doc(db, path), toFieldPath(first[0]), first[1], ...rest.flatMap(([k, v]) => [toFieldPath(k), v]))
      setStatus('saved')
    } catch (e) {
      console.error('save failed', e)
      pending.current = { ...updates, ...pending.current }
      setStatus('error')
    }
  }, [path])

  const write = useCallback(
    (updates: Record<string, unknown>) => {
      Object.assign(pending.current, updates)
      setStatus('saving')
      if (timer.current) window.clearTimeout(timer.current)
      timer.current = window.setTimeout(() => void flush(), delayMs)
    },
    [flush, delayMs],
  )

  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === 'hidden') void flush()
    }
    document.addEventListener('visibilitychange', onHide)
    window.addEventListener('pagehide', onHide)
    return () => {
      document.removeEventListener('visibilitychange', onHide)
      window.removeEventListener('pagehide', onHide)
      void flush()
      pendingWriters.delete(writerId.current)
    }
  }, [flush])

  return useMemo(() => ({ write, flush, status }), [write, flush, status])
}

export function useCallable<Req, Res>(name: string) {
  return useMemo(() => {
    const fn = httpsCallable<Req, Res>(functions, name)
    return async (data: Req): Promise<Res> => (await fn(data)).data
  }, [name])
}
